import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ActivityIcon,
  ArrowRight,
  Check,
  Code,
  Cpu,
  DatabaseIcon,
  FileTextIcon,
  KeyIcon,
  Layers,
  ListTreeIcon,
  LockIcon,
  type LucideIcon,
  Network,
  PlugIcon,
  RadarIcon,
  Radio,
  RefreshCwIcon,
  SearchIcon,
  Server,
  ServerCogIcon,
  ShieldCheckIcon,
  TagIcon,
  UsersIcon,
} from 'lucide-react'

import edgeArchitecture from '@/assets/edgedetail.png'
import edgeLogo from '@/assets/hivemq-bee-edge-neg.svg'
import { InsightCard, PageHeader, RevealStack, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/hivemqedge')({
  component: HiveMQEdgePage,
})

interface FeatureItemProps {
  title: string
  description: string
}

function FeatureItem({ title, description }: FeatureItemProps) {
  return (
    <HStack gap={3} align="flex-start">
      <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
        <Check size={14} color="var(--chakra-colors-green-600)" />
      </Box>
      <VStack align="start" gap={0}>
        <Text fontWeight="bold">{title}</Text>
        <Text fontSize="sm" color="content.secondary">
          {description}
        </Text>
      </VStack>
    </HStack>
  )
}

interface FeatureCardProps {
  icon: typeof Cpu
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}>
      <Card.Body p={5}>
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface ProtocolBadgeProps {
  name: string
}

function ProtocolBadge({ name }: ProtocolBadgeProps) {
  return (
    <Box px={3} py={1} bg="gray.100" borderRadius="full" _dark={{ bg: 'gray.700' }}>
      <Text fontSize="sm" fontWeight="medium">
        {name}
      </Text>
    </Box>
  )
}

interface InsightItem {
  accent: string
  colorPalette: string
  icon: LucideIcon
  title: string
  description: string
}

type AdapterStatus = 'Available' | 'New' | 'In Development'

interface AdapterItem {
  icon: LucideIcon
  title: string
  description: string
  status: AdapterStatus
}

// Left-accent + badge colour per adapter maturity, so the whole grid reads as
// established / newly landed / upcoming at a glance.
const adapterStatusStyle: Record<AdapterStatus, { accent: string; colorPalette: string }> = {
  Available: { accent: 'green.solid', colorPalette: 'green' },
  New: { accent: 'blue.solid', colorPalette: 'blue' },
  'In Development': { accent: 'orange.solid', colorPalette: 'orange' },
}

// One card per adapter in the set (mirrors the `protocols` list below), not
// just the newly landed ones.
const protocolAdapters: AdapterItem[] = [
  {
    icon: Network,
    title: 'OPC UA',
    status: 'Available',
    description:
      'Browse the address space and stream tags with full OPC UA quality context - the deepest reference implementation.',
  },
  {
    icon: Cpu,
    title: 'Modbus TCP',
    status: 'Available',
    description:
      'Poll Modbus TCP registers and coils and map them to tags published northbound to MQTT.',
  },
  {
    icon: Cpu,
    title: 'Modbus RTU',
    status: 'Available',
    description: 'Serial Modbus RTU support for legacy field devices over RS-485 / RS-232 links.',
  },
  {
    icon: ServerCogIcon,
    title: 'Siemens S7',
    status: 'Available',
    description: 'Connect Siemens S7 PLCs (S7-300/400/1200/1500) and stream process data to MQTT.',
  },
  {
    icon: Server,
    title: 'Beckhoff ADS',
    status: 'Available',
    description:
      'Read Beckhoff TwinCAT variables over the ADS protocol without touching control logic.',
  },
  {
    icon: Radio,
    title: 'HTTP',
    status: 'Available',
    description: 'Poll REST/HTTP endpoints on an interval and publish the responses as tags.',
  },
  {
    icon: Radio,
    title: 'MQTT-SN',
    status: 'Available',
    description: 'Bridge upstream MQTT and MQTT-SN sources into the unified namespace.',
  },
  {
    icon: DatabaseIcon,
    title: 'Databases (MySQL / MSSQL / PostgreSQL)',
    status: 'Available',
    description:
      'SQL queries mapped to tags; results published northbound to MQTT with no custom code.',
  },
  {
    icon: RadarIcon,
    title: 'BACnet',
    status: 'New',
    description:
      'UDP broadcast WHOIS/IAM discovers devices, addresses, instance numbers, and current values automatically.',
  },
  {
    icon: ActivityIcon,
    title: 'SNMP',
    status: 'In Development',
    description:
      'Polling and trap-based data collection from network infrastructure and ICS devices.',
  },
  {
    icon: PlugIcon,
    title: 'EtherNet/IP (CIP-ODVA)',
    status: 'In Development',
    description:
      'Originally custom-built for a customer, now being generalized into a standard capability.',
  },
]

const opcUaCapabilities: InsightItem[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: LockIcon,
    title: 'TLS Validation + Health Monitoring',
    description:
      'Configurable certificate validation levels; health checks with fault detection and immediate reconnect on failure.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: FileTextIcon,
    title: 'Metadata in MQTT Payloads',
    description:
      'Per-tag opt-in: statusCode, sourceTimestamp, serverTimestamp, picoseconds - full OPC UA quality context carried into MQTT.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: SearchIcon,
    title: 'Tag Browsing & Discovery',
    description:
      'Scan the address space; export/import up to 21 fields per tag with 5 conflict-resolution modes.',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: ListTreeIcon,
    title: 'Tag Schema',
    description:
      'Standardized data contracts reflecting the OPC UA type system: data types, access levels, and metadata.',
  },
  {
    accent: 'teal.solid',
    colorPalette: 'teal',
    icon: RefreshCwIcon,
    title: 'Exponential Backoff + Reliability',
    description:
      'Retry strategy prevents overwhelming servers during outages; a Service Fault listener forces immediate reconnect.',
  },
]

const tagManagement: InsightItem[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: TagIcon,
    title: 'Adapter-Namespaced Tags',
    description:
      'Tags scoped per adapter (AdapterName :: TagName); the same tag name can exist in multiple adapters - no global uniqueness requirement.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: ListTreeIcon,
    title: 'Tag Schema per Protocol',
    description:
      'Standardized data contracts per protocol (OPC UA, Modbus, S7, ADS); integrates with Data Hub for payload validation.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: SearchIcon,
    title: 'Tag Browsing',
    description:
      "Scan a device's full address space; export up to 21 fields, edit offline with any tool (spreadsheet, script, AI, CI/CD), import back with 5 conflict-resolution modes.",
  },
]

const securityCapabilities: InsightItem[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: KeyIcon,
    title: 'LDAP Authentication',
    description: 'Authenticate users against an existing LDAP directory.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: UsersIcon,
    title: 'RBAC - Three Built-in Roles',
    description: 'user / super / admin, assigned via LDAP group membership.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: ServerCogIcon,
    title: 'RBAC in Helm + Java 25 LTS runtime',
    description:
      'Role-based access control configurable in Helm, running on the Java 25 LTS runtime.',
  },
]

function HiveMQEdgePage() {
  const navigate = useNavigate()

  const features = [
    {
      icon: Server,
      title: 'Connect  -  Edge-Native Intelligence',
      description:
        'Deploy where the machines are  -  sub-second reaction times, no cloud dependency for operational decisions.',
    },
    {
      icon: Cpu,
      title: 'Connect  -  Protocol Bridge',
      description:
        'Bridge OPC UA, Modbus, BACnet, S7, and more to MQTT without touching PLCs or control logic.',
    },
    {
      icon: Layers,
      title: 'Contextualize  -  Feed the UNS',
      description:
        'Feed governed, structured data into the Unified Namespace  -  eliminate data silos at the source.',
    },
    {
      icon: Radio,
      title: 'Repeatable Onboarding',
      description:
        'Every new site is a rollout, not a project. Connect, discover, align, compute, dashboard.',
    },
    {
      icon: Code,
      title: 'Analyze  -  Edge Expressions',
      description:
        'Compute KPIs at the source with edge expressions  -  no ETL scripts, no cloud round-trips.',
    },
    {
      icon: Network,
      title: 'Act  -  Secure Connectivity',
      description:
        'Outbound-only connectivity from edge to platform  -  IT security approves faster, deployment timelines shrink.',
    },
  ]

  const protocols = [
    'OPC UA',
    'Modbus TCP',
    'Modbus RTU',
    'S7',
    'ADS',
    'HTTP',
    'MQTT-SN',
    'EtherNet/IP',
    'BACnet',
    'SNMP',
    'Databases',
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        centered
        title="HiveMQ Edge"
        subtitle="The software-based edge gateway that bridges OT and IT at the source - connect legacy equipment to your industrial data platform without disrupting operations."
      />

      <RevealStack align="stretch" gap={10}>
        {/* Hero identity */}
        <VStack gap={5} textAlign="center">
          <Image src={edgeLogo} alt="HiveMQ" h="90px" objectFit="contain" />
          <HStack gap={2} flexWrap="wrap" justify="center">
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Edge-Native
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                OT/IT Convergence
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                UNS Ready
              </Text>
            </Box>
          </HStack>
        </VStack>

        {/* Edge Architecture Image */}
        <Card.Root overflow="hidden">
          <Card.Body p={6}>
            <Box display="flex" justifyContent="center">
              <Image
                src={edgeArchitecture}
                alt="HiveMQ Edge Architecture"
                maxH="400px"
                objectFit="contain"
                borderRadius="lg"
              />
            </Box>
          </Card.Body>
        </Card.Root>

        {/* Why Edge */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Connect at the Source"
            description="Deploy edge-native intelligence where the machines are - bridge industrial protocols to MQTT and feed governed data into your industrial data platform."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {features.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Supported Protocols */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Industrial Protocol Support"
            description="Connect to a wide range of industrial protocols out of the box"
          />
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={8} alignItems="center">
            <Card.Root>
              <Card.Body p={6}>
                <VStack align="start" gap={4}>
                  <HStack gap={2}>
                    <Box p={2} bg="brand.solid" borderRadius="lg">
                      <Radio size={20} />
                    </Box>
                    <Heading size="md">Supported Protocols</Heading>
                  </HStack>
                  <HStack gap={2} flexWrap="wrap">
                    {protocols.map((protocol) => (
                      <ProtocolBadge key={protocol} name={protocol} />
                    ))}
                  </HStack>
                  <Text fontSize="sm" color="content.secondary">
                    And many more through the extensible adapter framework...
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body p={6}>
                <VStack gap={4}>
                  <Heading size="md">Key Capabilities</Heading>
                  <VStack align="start" gap={3} w="100%">
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Protocol translation to MQTT</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Local data buffering and store-and-forward</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Web-based configuration UI</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">REST API for automation</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Sparkplug B support for UNS</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Protocol Adapters */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Protocol Adapters"
            description="One card per adapter in the set - from established protocols to adapters landing now and in development."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            {protocolAdapters.map((adapter) => (
              <InsightCard
                key={adapter.title}
                accent={adapterStatusStyle[adapter.status].accent}
                colorPalette={adapterStatusStyle[adapter.status].colorPalette}
                icon={adapter.icon}
                badgeLabel={adapter.status}
                title={adapter.title}
                description={adapter.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* OPC UA Reference Implementation */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="OPC UA: The Reference Implementation"
            description="OPC UA is where HiveMQ Edge's adapter capabilities go deepest first, before rolling out to other protocols."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {opcUaCapabilities.map((capability) => (
              <InsightCard
                key={capability.title}
                accent={capability.accent}
                colorPalette={capability.colorPalette}
                icon={capability.icon}
                badgeLabel="OPC UA"
                title={capability.title}
                description={capability.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Tag Management at Industrial Scale */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Tag Management at Industrial Scale"
            description="Consistent tag scoping, schema, and browsing - built to work the same way across every adapter."
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
            {tagManagement.map((item) => (
              <InsightCard
                key={item.title}
                accent={item.accent}
                colorPalette={item.colorPalette}
                icon={item.icon}
                badgeLabel="Tags"
                title={item.title}
                description={item.description}
              />
            ))}
          </SimpleGrid>
          <Text fontSize="sm" color="content.tertiary" fontStyle="italic">
            What&apos;s next: Tag Browsing expanding beyond OPC UA (Modbus, BACnet, EtherNet/IP);
            OPC UA Alarms &amp; Conditions - event-driven alarm data alongside sensor values.
          </Text>
        </VStack>

        {/* Security & High Availability */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Security & High Availability"
            description="Enterprise authentication and authorization, plus a production-validated high-availability configuration."
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
            {securityCapabilities.map((item) => (
              <InsightCard
                key={item.title}
                accent={item.accent}
                colorPalette={item.colorPalette}
                icon={item.icon}
                badgeLabel="Security"
                title={item.title}
                description={item.description}
              />
            ))}
          </SimpleGrid>
          <Text fontSize="sm" color="content.tertiary" fontStyle="italic">
            What&apos;s next: OpenID Authentication.
          </Text>

          <Card.Root bg="bg.subtle">
            <Card.Body p={6}>
              <VStack align="start" gap={4}>
                <HStack gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <ShieldCheckIcon size={20} />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Text fontSize="xs" fontWeight="bold" color="content.tertiary">
                      HA
                    </Text>
                    <Heading size="md">Production-validated at 50,000+ msg/sec.</Heading>
                  </VStack>
                </HStack>
                <Text fontSize="sm" color="content.secondary">
                  Two Edge arms run active/active, both ingesting OPC UA simultaneously, feeding a
                  dedup Edge (using Data Hub scripts) before data reaches the HiveMQ broker, with
                  Kubernetes-native failover across the whole chain.
                </Text>
                <VStack align="start" gap={3} w="100%">
                  <FeatureItem
                    title="Redundant Ingestion"
                    description="Both arms ingest OPC UA simultaneously - one can fail without data loss or a gap in the time series."
                  />
                  <FeatureItem
                    title="Deduplication at Scale"
                    description="Data Hub scripts eliminate duplicate messages before the broker."
                  />
                  <FeatureItem
                    title="Kubernetes-Native Failover"
                    description="Designed for production, not just evaluation."
                  />
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Use Cases */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Edge to Enterprise"
            description="See how HiveMQ Edge enables connectivity in real-world scenarios"
          />
          <Card.Root w="100%">
            <Card.Body p={6}>
              <VStack align="start" gap={4}>
                <FeatureItem
                  title="Brownfield Integration"
                  description="Connect legacy industrial equipment without disrupting operations  -  no PLC changes, no topic renaming, no OT downtime."
                />
                <FeatureItem
                  title="Unified Namespace Implementation"
                  description="Create a single source of truth for all operational data using ISA-95 topic structures."
                />
                <FeatureItem
                  title="Edge Intelligence"
                  description="Compute and govern data at the source  -  edge expressions deliver insights where they matter without cloud round-trips."
                />
                <FeatureItem
                  title="Multi-Site Connectivity"
                  description="Connect multiple facilities through MQTT bridges to a central HiveMQ cluster."
                />
                <FeatureItem
                  title="OT/IT Convergence"
                  description="Enable IT applications to consume OT data through standard MQTT interfaces."
                />
                <FeatureItem
                  title="Acquisition Onboarding"
                  description="New site to scoreboard in hours  -  connect, discover, align to existing Data Models, compute KPIs, dashboard."
                />
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Ready to explore use cases?</Heading>
                <Text color="content.secondary">
                  See HiveMQ Edge in action across different industries
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/' })}>
                Explore Industries
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </RevealStack>
    </Box>
  )
}
